import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { CheckCircle, MusicNote } from "@phosphor-icons/react";

const AUDIO_LOADING = [
  "Afinando guitarras...",
  "Grabando voces...",
  "Mezclando instrumentos...",
  "Aplicando los toques finales...",
];

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const sessionId = params.get("session_id");
  const songId = params.get("song_id");

  const [status, setStatus] = useState("checking"); // checking | paid | failed | expired
  const [msgIdx, setMsgIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setMsgIdx(i => (i + 1) % AUDIO_LOADING.length), 2000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    let attempts = 0;
    const poll = async () => {
      attempts++;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (data.status === "expired") { setStatus("expired"); return; }
      } catch (e) {
        if (process.env.NODE_ENV === "development") {
          console.warn("[PaymentSuccess] poll failed:", e?.response?.status || e?.message);
        }
      }
      if (attempts >= 15) { setStatus("failed"); return; }
      setTimeout(poll, 2000);
    };
    poll();
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />
      <div className="max-w-md mx-auto px-4 sm:px-6 py-16 text-center">
        {status === "checking" && (
          <div data-testid="payment-checking">
            <div className="soundwave mx-auto mb-6">
              <span></span><span></span><span></span><span></span><span></span><span></span><span></span>
            </div>
            <p className="font-display text-2xl font-bold pulse-title">{AUDIO_LOADING[msgIdx]}</p>
            <p className="text-white/40 text-sm mt-3">Confirmando tu pago y generando audio…</p>
          </div>
        )}
        {status === "paid" && (
          <div data-testid="payment-success">
            <div className="w-20 h-20 rounded-full bg-[#B4F34A] mx-auto flex items-center justify-center mb-6">
              <CheckCircle weight="fill" size={42} className="text-black" />
            </div>
            <h1 className="font-display text-4xl font-black mb-3">¡Tu canción está lista!</h1>
            <p className="text-white/60 mb-8">Ya puedes escucharla y descargarla con tu portada incrustada.</p>
            <Link
              to={`/song/${songId}`}
              className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-[#FFB800] hover:bg-[#E6A600] text-black font-semibold active:scale-95 transition-all"
              data-testid="success-go-song"
            >
              Escuchar mi canción <MusicNote weight="fill" size={18} />
            </Link>
          </div>
        )}
        {(status === "failed" || status === "expired") && (
          <div data-testid="payment-failed">
            <p className="font-display text-2xl font-bold mb-3 text-[#FF4B00]">Algo salió mal</p>
            <p className="text-white/60 mb-6">No pudimos confirmar tu pago. Intenta de nuevo desde tu canción.</p>
            <Link to={`/create?song_id=${songId}`} className="inline-block px-6 py-3 rounded-full bg-white/5 border border-white/10">
              Intentar de nuevo
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
